import React, { useContext, useEffect, useState, useCallback } from 'react';
import { Button, Drawer, Space, Table, Tag, Tooltip, Input } from 'antd';
import { useNavigate } from 'react-router-dom';
import { DeleteOutlined, EditOutlined, EyeOutlined } from '@ant-design/icons';
import { toast } from 'react-toastify';
import axios from 'axios';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import couponService from '../../services/coupon';
import GlobalContainer from '../../components/global-container';
import { Context } from '../../context/context';
import CustomModal from '../../components/modal';
import { addMenu } from '../../redux/slices/menu';

const Coupon = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { setIsModalVisible } = useContext(Context);

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [couponId, setCouponId] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [historyVisible, setHistoryVisible] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyData, setHistoryData] = useState([]);
  const [selectedCoupon, setSelectedCoupon] = useState(null);

  const OPERATOR_CATEGORIES = [
    { value: 'All', label: 'All' },
    { value: 'Prepaid', label: 'Prepaid' },
    { value: 'DTH', label: 'DTH' },
    { value: 'Electricity', label: 'Electricity' },
    { value: 'Broadband', label: 'Broadband' },
    { value: 'Gas', label: 'Gas - Piped Gas' },
    { value: 'Fastag', label: 'Fastag' },
    { value: 'Landline', label: 'Landline' },
    { value: 'Postpaid', label: 'Postpaid' },
    { value: 'Broadband Postpaid', label: 'Broadband Postpaid' },
    { value: 'Insurance', label: 'Insurance' },
    { value: 'EMI Payment', label: 'EMI Payment' },
    { value: 'LPG Booking', label: 'LPG Booking' },
  ];

  const fetchCoupons = useCallback(async () => {
    const token = localStorage.getItem('token');
    setLoading(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/api/v1/dashboard/admin/coupons`,
        {
          params: {
            search: searchQuery,
          },
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
        }
      );

      if (response.data.success) {
        setData(response.data.data);
      }
    } catch (error) {
      console.error('Fetch Error:', error);
      toast.error(t('error.fetching'));
    } finally {
      setLoading(false);
    }
  }, [t, searchQuery]);

  const deleteCoupon = useCallback(async () => {
    try {
      await axios.delete(
        `${process.env.REACT_APP_BASE_URL}/api/v1/dashboard/admin/coupons/${couponId}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
        }
      );

      toast.success(t('successfully.deleted'));
      setCouponId(null);
      setIsModalVisible(false);
      fetchCoupons();
    } catch (error) {
      console.error('Delete Error:', error);
      toast.error(t('error.deleting'));
    }
  }, [couponId, setIsModalVisible, fetchCoupons, t]);

  const goToEdit = useCallback(
    (row) => {
      dispatch(
        addMenu({
          url: `coupon/${row.id}`,
          id: 'coupon_edit',
          name: t('edit.coupon'),
        })
      );
      navigate(`/coupon/${row.id}`);
    },
    [dispatch, navigate, t]
  );

  const getCategoryLabel = useCallback((cat) => {
    const category = OPERATOR_CATEGORIES.find((c) => c.value === cat);
    return category ? category.label : cat;
  }, []);

  const getCategoryColor = useCallback((cat) => {
    const colorMap = {
      All: 'blue',
      Prepaid: 'green',
      DTH: 'purple',
      Electricity: 'red',
      Broadband: 'orange',
      Gas: 'cyan',
      Fastag: 'magenta',
      Landline: 'geekblue',
      Postpaid: 'volcano',
      'Broadband Postpaid': 'gold',
      Insurance: 'lime',
      'EMI Payment': 'pink',
      'LPG Booking': 'teal',
    };
    return colorMap[cat] || 'default';
  }, []);

  const columns = [
    {
      title: '#',
      dataIndex: 'id',
      width: 50,
      render: (_, __, index) => index + 1,
    },
    {
      title: t('name'),
      dataIndex: 'name',
      width: 120,
      ellipsis: true,
      render: (name) => (
        <Tooltip title={name}>
          {name}
        </Tooltip>
      ),
    },
    {
      title: t('category'),
      dataIndex: 'cat',
      width: 110,
      render: (cat) => (
        <Tag color={getCategoryColor(cat)}>{getCategoryLabel(cat)}</Tag>
      ),
    },
    {
      title: t('type') + ' / ' + t('Price/%'),
      dataIndex: 'price',
      width: 110,
      render: (price, record) => {
        const label = record.type.toLowerCase() === 'percent'
          ? `${price}%`
          : `₹${price.toFixed(2)}`;
        return (
          <span>
            <Tag>{record.type.toUpperCase()}</Tag> {label}
          </span>
        );
      },
    },
    {
      title: t('Min Value'),
      dataIndex: 'min_avail_amount',
      width: 100,
      render: (value) => `₹${value?.toFixed(2) || '0.00'}`,
    },
    {
      title: t('max_discount'),
      dataIndex: 'max_discount_amount',
      width: 100,
      render: (value) => (value ? `₹${Number(value).toFixed(2)}` : '-'),
    },
    {
      title: t('usage'),
      dataIndex: 'used_count',
      width: 90,
      render: (_, row) => `${row.used_count ?? 0} / ${row.qty ?? 0}`,
    },
    {
      title: t('max_uses_per_user'),
      dataIndex: 'max_uses_per_user',
      width: 80,
      render: (value) => value ?? 1,
    },
    {
      title: t('expired.at'),
      dataIndex: 'expired_at',
      width: 110,
      render: (expired_at) => moment(expired_at).format('YYYY-MM-DD'),
    },
    {
      title: t('status'),
      dataIndex: 'status_label',
      width: 90,
      render: (status) => {
        const color = status === 'active' ? 'green' : status === 'expired' ? 'orange' : 'red';
        return <Tag color={color}>{status?.toUpperCase()}</Tag>;
      },
    },
    {
      title: t('description'),
      dataIndex: 'description',
      width: 150,
      ellipsis: true,
      render: (description) => (
        <Tooltip title={description}>
          {description}
        </Tooltip>
      ),
    },
    {
      title: t('options'),
      dataIndex: 'options',
      width: 130,
      fixed: 'right',
      render: (_, row) => (
        <Space>
          <Tooltip title={t('edit')}>
            <Button
              type='primary'
              size='small'
              icon={<EditOutlined />}
              onClick={() => goToEdit(row)}
            />
          </Tooltip>
          <Tooltip title={t('delete')}>
            <Button
              danger
              size='small'
              icon={<DeleteOutlined />}
              onClick={() => {
                setCouponId(row.id);
                setIsModalVisible(true);
              }}
            />
          </Tooltip>
          <Tooltip title={t('view.usage')}>
            <Button
              size='small'
              icon={<EyeOutlined />}
              onClick={() => {
                setSelectedCoupon(row);
                fetchCouponUsages(row.id);
                setHistoryVisible(true);
              }}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  const fetchCouponUsages = useCallback(async (id) => {
    setHistoryLoading(true);
    try {
      const response = await couponService.usages(id);
      if (response.data.success) {
        setHistoryData(response.data.data);
      }
    } catch (error) {
      console.error('Fetch Usage Error:', error);
      toast.error(t('error.fetching'));
    } finally {
      setHistoryLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchCoupons();
  }, [fetchCoupons]);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      fetchCoupons();
    }, 500); // 500ms delay

    return () => clearTimeout(delayDebounceFn);
  }, [searchQuery, fetchCoupons]);

  return (
    <GlobalContainer
      headerTitle={t('coupons')}
      navLInkTo='/coupon/add'
      buttonTitle={t('add.coupon')}
    >
      <div style={{ marginBottom: '10px' }}>
        <Input.Search
          placeholder={t('search.coupons')}
          allowClear
          enterButton
          size='large'
          onSearch={(value) => setSearchQuery(value)}
          style={{ maxWidth: 400 }}
        />
      </div>
      <Table
        columns={columns}
        rowKey={(record) => record.id}
        dataSource={data}
        scroll={{ x: 1300 }}
        size='small'
        pagination={{
          total: data.length,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total) => `Total ${total} coupons`,
        }}
        loading={loading}
      />
      <CustomModal click={deleteCoupon} text={t('delete.coupon')} />

      <Drawer
        title={`${t('coupon.usage.history')} - ${selectedCoupon?.name || ''}`}
        placement='right'
        width={520}
        onClose={() => setHistoryVisible(false)}
        open={historyVisible}
      >
        <p>
          {t('total.used')}: {selectedCoupon?.used_count ?? 0}
        </p>
        <Table
          columns={[
            { title: t('user'), dataIndex: 'name' },
            { title: t('phone'), dataIndex: 'phone' },
            { title: t('usage.count'), dataIndex: 'usage_count' },
          ]}
          rowKey={(record) => record.user_id}
          dataSource={historyData}
          loading={historyLoading}
          pagination={false}
        />
      </Drawer>
    </GlobalContainer>
  );
};

export default Coupon;
